import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnimationEvent } from 'react';

const ROUTE_MODAL_CLOSE_FALLBACK_MS = 200;

export type RouteModalAnimationEvent = Pick<AnimationEvent<HTMLElement>, 'target' | 'currentTarget'> &
    Partial<Pick<AnimationEvent<HTMLElement>, 'animationName'>>;

export type RouteModalOpenState = {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onAnimationEnd: (event: RouteModalAnimationEvent) => void;
};

/** Keeps a route modal mounted until its exit animation has finished. */
export const useRouteModalOpenState = (close: () => void): RouteModalOpenState => {
    const [isOpen, setIsOpen] = useState(true);
    const hasClosedRef = useRef(false);

    /** Prevents the animation and fallback from navigating twice in the same frame. */
    const closeOnce = useCallback(() => {
        if (hasClosedRef.current) return;
        hasClosedRef.current = true;
        close();
    }, [close]);

    /** Starts the exit transition without navigating away from the route. */
    const onOpenChange = useCallback((nextIsOpen: boolean) => {
        if (!nextIsOpen) setIsOpen(false);
    }, []);

    /** Ignores entering animations and events bubbling from modal content. */
    const onAnimationEnd = useCallback(
        (event: RouteModalAnimationEvent) => {
            if (isOpen || event.target !== event.currentTarget || event.animationName === 'enter') return;
            closeOnce();
        },
        [isOpen, closeOnce],
    );

    useEffect(() => {
        if (isOpen) return;

        const timeout = window.setTimeout(closeOnce, ROUTE_MODAL_CLOSE_FALLBACK_MS);
        return () => window.clearTimeout(timeout);
    }, [isOpen, closeOnce]);

    return { isOpen, onOpenChange, onAnimationEnd };
};

export default useRouteModalOpenState;
